/**
 * Health dashboard scoring, ported from updateHealthDashboard in index.html:
 * a sensor scores green 100, yellow 70, red 40; a component is the mean of
 * its sensors (85 when it has none); the overall score is the mean of the
 * components; any component under 60 makes the system critical, 60 to 79
 * warning, else healthy. Here every subsystem with data is a component.
 */
import type { StatusColour, Subsystem } from '../../types';
import type { ItemColourLookup } from './cardTree';

export type HealthBand = 'healthy' | 'warning' | 'critical';

export const SCORE: Record<StatusColour, number> = { green: 100, yellow: 70, red: 40 };
export const NO_SENSOR_SCORE = 85;

export function sensorScore(colour: StatusColour | null): number {
  return colour ? SCORE[colour] : SCORE.green;
}

export function componentScore(colours: Array<StatusColour | null>): number {
  if (colours.length === 0) return NO_SENSOR_SCORE;
  return Math.round(colours.reduce((sum, c) => sum + sensorScore(c), 0) / colours.length);
}

export function overallScore(scores: number[]): number {
  if (scores.length === 0) return 100;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

export function bandFor(componentScores: number[]): HealthBand {
  if (componentScores.some((s) => s < 60)) return 'critical';
  if (componentScores.some((s) => s < 80)) return 'warning';
  return 'healthy';
}

export function scoreBand(score: number): HealthBand {
  return score < 60 ? 'critical' : score < 80 ? 'warning' : 'healthy';
}

export interface ComponentHealth {
  id: string;
  label: string;
  path: string;
  score: number;
  colour: StatusColour;
  sensors: number;
}

/** One entry per subsystem that carries data items, depth first. */
export function scoreSubsystems(subsystems: Subsystem[], colourOf: ItemColourLookup): ComponentHealth[] {
  const out: ComponentHealth[] = [];
  const visit = (s: Subsystem, trail: string[]) => {
    const path = [...trail, s.label];
    if (s.data?.length) {
      const colours = s.data.map((d) => colourOf(s.id, d.id));
      const score = componentScore(colours);
      out.push({ id: s.id, label: s.label, path: path.join(' > '), score, colour: score < 60 ? 'red' : score < 80 ? 'yellow' : 'green', sensors: colours.length });
    }
    for (const child of s.childs ?? []) visit(child, path);
  };
  for (const s of subsystems) visit(s, []);
  return out;
}
